
export type LayoutMode = 'landscape' | 'portrait';

export interface LayoutMetrics {
  width: number;
  height: number;
  mode: LayoutMode;
  scale: number;

  // Board metrics
  boardSize: number;
  boardX: number;
  boardY: number;
  tileSize: number;

  // Card metrics
  cardWidth: number;
  cardHeight: number;
  cardsPerPlayer: number;

  // HUD areas
  player1HUD: { x: number; y: number; width: number; height: number };
  player2HUD: { x: number; y: number; width: number; height: number };

  // Content bounds (the fixed aspect ratio area)
  contentX: number;
  contentY: number;
  contentWidth: number;
  contentHeight: number;
}

// Fixed layout constants
const CARDS_PER_PLAYER = 3;
const CARD_ASPECT_RATIO = 0.7; // width / height
const BOARD_TILES = 5;
const TILE_GAP = 4;
const PADDING = 16;
const CARD_GAP = 8;

/**
 * Manages responsive layout calculations for the game
 * Uses a fixed aspect ratio approach where the game content
 * is centered with extra space on the sides
 */
export class LayoutManager {
  private metrics: LayoutMetrics;
  private listeners: Set<(metrics: LayoutMetrics) => void> = new Set();

  constructor() {
    this.metrics = this.calculateMetrics(window.innerWidth, window.innerHeight);
  }

  /**
   * Calculate layout metrics based on screen dimensions
   */
  private calculateMetrics(width: number, height: number): LayoutMetrics {
    const mode: LayoutMode = height > width ? 'portrait' : 'landscape';

    if (mode === 'landscape') {
      return this.calculateLandscapeMetrics(width, height);
    } else {
      return this.calculatePortraitMetrics(width, height);
    }
  }

  /**
   * Landscape mode: Player 1 cards on left, board in center, Player 2 cards on right
   */
  private calculateLandscapeMetrics(width: number, height: number): LayoutMetrics {
    // Available height for content (minus padding)
    const availableHeight = height - PADDING * 2;

    // Card height = 1/3 of available height (3 cards stacked)
    const cardHeight = (availableHeight - CARD_GAP * 2) / CARDS_PER_PLAYER;
    const cardWidth = cardHeight * CARD_ASPECT_RATIO;

    // Board size = available height (square)
    const maxBoardSize = availableHeight;

    // Total content width = card column + gap + board + gap + card column
    const contentWidth = cardWidth + PADDING + maxBoardSize + PADDING + cardWidth;
    const contentHeight = availableHeight;

    // Scale down if content doesn't fit width
    let scale = 1;
    if (contentWidth > width - PADDING * 2) {
      scale = (width - PADDING * 2) / contentWidth;
    }

    const scaledCardWidth = cardWidth * scale;
    const scaledCardHeight = cardHeight * scale;
    const scaledBoardSize = maxBoardSize * scale;
    const scaledContentWidth = contentWidth * scale;
    const scaledContentHeight = contentHeight * scale;

    // Center content
    const contentX = (width - scaledContentWidth) / 2;
    const contentY = (height - scaledContentHeight) / 2;

    // Position elements within content area
    const player1HUDX = contentX;
    const player1HUDY = contentY;
    const boardX = contentX + scaledCardWidth + PADDING * scale;
    const boardY = contentY;
    const player2HUDX = boardX + scaledBoardSize + PADDING * scale;
    const player2HUDY = contentY;

    const tileSize = (scaledBoardSize - TILE_GAP * (BOARD_TILES - 1)) / BOARD_TILES;

    return {
      width,
      height,
      mode: 'landscape',
      scale,
      boardSize: scaledBoardSize,
      boardX,
      boardY,
      tileSize,
      cardWidth: scaledCardWidth,
      cardHeight: scaledCardHeight,
      cardsPerPlayer: CARDS_PER_PLAYER,
      player1HUD: {
        x: player1HUDX,
        y: player1HUDY,
        width: scaledCardWidth,
        height: scaledContentHeight,
      },
      player2HUD: {
        x: player2HUDX,
        y: player2HUDY,
        width: scaledCardWidth,
        height: scaledContentHeight,
      },
      contentX,
      contentY,
      contentWidth: scaledContentWidth,
      contentHeight: scaledContentHeight,
    };
  }

  /**
   * Portrait mode: Player 2 cards on top (rotated), board in center, Player 1 cards on bottom
   */
  private calculatePortraitMetrics(width: number, height: number): LayoutMetrics {
    // Available width for content (minus padding)
    const availableWidth = width - PADDING * 2;

    // Card width = 1/3 of available width (3 cards side by side)
    const cardWidth = (availableWidth - CARD_GAP * 2) / CARDS_PER_PLAYER;
    const cardHeight = cardWidth / CARD_ASPECT_RATIO;

    // Board size = available width (square)
    const maxBoardSize = availableWidth;

    // Total content height = card row + gap + board + gap + card row
    const contentHeight = cardHeight + PADDING + maxBoardSize + PADDING + cardHeight;
    const contentWidth = availableWidth;

    // Scale down if content doesn't fit height
    let scale = 1;
    if (contentHeight > height - PADDING * 2) {
      scale = (height - PADDING * 2) / contentHeight;
    }

    const scaledCardWidth = cardWidth * scale;
    const scaledCardHeight = cardHeight * scale;
    const scaledBoardSize = maxBoardSize * scale;
    const scaledContentWidth = contentWidth * scale;
    const scaledContentHeight = contentHeight * scale;

    // Center content
    const contentX = (width - scaledContentWidth) / 2;
    const contentY = (height - scaledContentHeight) / 2;

    // Position elements within content area
    // Player 2 on TOP (rotated 180 degrees)
    const player2HUDX = contentX;
    const player2HUDY = contentY;
    const boardX = contentX;
    const boardY = contentY + scaledCardHeight + PADDING * scale;
    // Player 1 on BOTTOM
    const player1HUDX = contentX;
    const player1HUDY = boardY + scaledBoardSize + PADDING * scale;

    const tileSize = (scaledBoardSize - TILE_GAP * (BOARD_TILES - 1)) / BOARD_TILES;

    return {
      width,
      height,
      mode: 'portrait',
      scale,
      boardSize: scaledBoardSize,
      boardX,
      boardY,
      tileSize,
      cardWidth: scaledCardWidth,
      cardHeight: scaledCardHeight,
      cardsPerPlayer: CARDS_PER_PLAYER,
      player1HUD: {
        x: player1HUDX,
        y: player1HUDY,
        width: scaledContentWidth,
        height: scaledCardHeight,
      },
      player2HUD: {
        x: player2HUDX,
        y: player2HUDY,
        width: scaledContentWidth,
        height: scaledCardHeight,
      },
      contentX,
      contentY,
      contentWidth: scaledContentWidth,
      contentHeight: scaledContentHeight,
    };
  }

  /**
   * Update metrics and notify listeners
   */
  onResize(width: number, height: number): void {
    this.metrics = this.calculateMetrics(width, height);
    this.notifyListeners();
  }

  /**
   * Get current layout metrics
   */
  getMetrics(): LayoutMetrics {
    return this.metrics;
  }

  /**
   * Subscribe to layout changes
   */
  subscribe(listener: (metrics: LayoutMetrics) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener(this.metrics);
    }
  }

  /**
   * Convert board position (row, col) to pixel coordinates
   */
  boardToPixel(row: number, col: number): { x: number; y: number } {
    const { boardX, boardY, tileSize } = this.metrics;
    const gap = TILE_GAP;

    return {
      x: boardX + col * (tileSize + gap) + tileSize / 2,
      y: boardY + row * (tileSize + gap) + tileSize / 2,
    };
  }

  /**
   * Get the center point of the screen
   */
  getCenter(): { x: number; y: number } {
    return {
      x: this.metrics.width / 2,
      y: this.metrics.height / 2,
    };
  }
}
